/**
 * Assistant markdown: GFM, sanitized, in a compact `prose` block themed to the ink ramp.
 * `MarkdownBlock` takes a string (the delivered card's summary); `MessageMarkdown` reads a
 * message's text part, live while it streams.
 */
import type React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { INTERNAL, useMessagePartText } from "@myela/ai-core-react/primitives";

const { useSmooth } = INTERNAL;

/**
 * The reveal for streamed text. The host writes the reply stream in 150 ms batches
 * (`UI_BATCH_MS` in @myela/ai-core/host); a 200 ms drain types each batch out before the next
 * lands, so the text reads as typing, not jumps.
 */
export const STREAM_SMOOTH = { drainMs: 200 };

// react-markdown's default schema is extended with GFM (tables, strikethrough,
// task lists) and hardened with rehype-sanitize so model output can never inject
// active markup. Assistant turns return markdown; user turns stay plain text.
const MARKDOWN_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeSanitize];

// Cap inline images so a model-supplied URL can't blow out the panel width.
// Tables get a fluid responsive layout (no horizontal scroll): `table-fixed`
// shares container width across columns, `break-words` on each cell wraps long
// values (cuid-style ids, business names, addresses) instead of stretching the
// table. This is critical in the narrow floating popup (~310px) where a
// natural-width GFM table would otherwise blow past the bubble's `max-w-*`.
const markdownComponents: Components = {
  img: ({ ...props }) => <img {...props} className="max-w-full h-auto rounded-md" />,
  table: ({ ...props }) => (
    <table
      {...props}
      className="w-full table-fixed border-collapse text-[11.5px] leading-[1.35]"
    />
  ),
  th: ({ ...props }) => (
    <th
      {...props}
      className="text-left font-medium align-top p-1 break-words border-b border-[var(--ink-200)]"
    />
  ),
  td: ({ ...props }) => (
    <td
      {...props}
      className="align-top p-1 break-words border-b border-[var(--ink-100)]"
    />
  ),
};

/**
 * Assistant-message text slot: renders the part's markdown (GFM, sanitized) in a
 * compact Tailwind-Typography `prose` block, reading the live text via
 * `useMessagePartText()` so it updates token-by-token during streaming.
 */
// Tailwind Typography paints text via its own `--tw-prose-*` variables, which
// don't respect our theme tokens — in dark mode they stay a dark gray and turn
// the bubble unreadable. Re-bind them to the ink ramp so prose tracks the theme.
const PROSE_THEME_VARS = {
  "--tw-prose-body": "var(--ink-800)",
  "--tw-prose-headings": "var(--ink-900)",
  "--tw-prose-lead": "var(--ink-700)",
  "--tw-prose-links": "var(--accent-600)",
  "--tw-prose-bold": "var(--ink-900)",
  "--tw-prose-counters": "var(--ink-500)",
  "--tw-prose-bullets": "var(--ink-500)",
  "--tw-prose-hr": "var(--ink-200)",
  "--tw-prose-quotes": "var(--ink-700)",
  "--tw-prose-quote-borders": "var(--ink-200)",
  "--tw-prose-captions": "var(--ink-500)",
  "--tw-prose-code": "var(--ink-900)",
  "--tw-prose-pre-code": "var(--ink-100)",
  "--tw-prose-pre-bg": "var(--ink-900)",
  "--tw-prose-th-borders": "var(--ink-200)",
  "--tw-prose-td-borders": "var(--ink-100)",
} as React.CSSProperties;

/** Model markdown as a sanitized GFM `prose` block; the delivered card reuses it. */
export function MarkdownBlock({ text }: { text: string }) {
  return (
    <div
      className="prose prose-sm max-w-none prose-p:my-1.5 prose-headings:my-2 prose-pre:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-table:my-2 break-words"
      style={PROSE_THEME_VARS}
    >
      <ReactMarkdown
        remarkPlugins={MARKDOWN_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={markdownComponents}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export const MessageMarkdown: React.FC = () => {
  const { text } = useSmooth(useMessagePartText(), STREAM_SMOOTH);
  return <MarkdownBlock text={text} />;
};
